import React, { useReducer } from 'react';
import colors from '../../helpers/colors';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import Collapsible from '../Collapsible';
import { GridItem } from '../Grid';
import Icon from '../Icon';
import {
  GridWithMargins,
  StyledCollapseContainer,
  StyledCollapseText,
  StyledHeader,
  StyledHeaderTitle,
  StyledHeadingIcon,
  StyledPanel,
} from './styles';

export type Heading = {
  text: string;
  icon?: string;
};

export type Collapse = {
  showText: string;
  hideText: string;
  displayInitially?: boolean;
  onCollapse?: () => void;
};

export type InfoBannerProps = {
  alignLeft?: boolean;
  heading?: Heading;
  collapse?: Collapse;
  children?: React.ReactNode;
} & ComponentProps;

const InfoBanner: React.FC<InfoBannerProps> = ({
  alignLeft,
  heading,
  collapse,
  children,
  ...props
}) => {
  const [isCollapsed, toggleCollapse] = useReducer(
    (state) => !state,
    collapse ? !!collapse.displayInitially : true
  );
  return (
    <GridWithMargins {...componentProps(props)} alignLeft={alignLeft}>
      <GridItem desktop={10} tabletLandscape={10} tabletPortrait={8} mobile={4} focusable>
        <StyledPanel
          hasHeading={!!heading}
          borderShadows={{ displayOutset: true }}
          isCollapsed={isCollapsed}>
          {(!!heading || !!collapse) && (
            <StyledHeader>
              {!!heading && (
                <StyledHeaderTitle data-testid="heading">
                  {!!heading.icon && (
                    <StyledHeadingIcon
                      name={heading.icon}
                      size="medium"
                      color={colors.core01}
                    />
                  )}
                  {heading.text}
                </StyledHeaderTitle>
              )}
              {!!collapse && (
                <StyledCollapseContainer
                  tabIndex={0}
                  data-testid="collapse"
                  onClick={() => {
                    toggleCollapse();
                    collapse.onCollapse?.();
                  }}>
                  <StyledCollapseText>
                    {isCollapsed ? collapse.hideText : collapse.showText}
                  </StyledCollapseText>
                  <Icon
                    name="arrow-down"
                    size="small"
                    flipY={isCollapsed}
                    color={colors.neutral03}
                  />
                </StyledCollapseContainer>
              )}
            </StyledHeader>
          )}
          <Collapsible isOpened={isCollapsed}>{children}</Collapsible>
        </StyledPanel>
      </GridItem>
    </GridWithMargins>
  );
};

export default applyComponentTheme(InfoBanner, 'InfoBanner');
